import { useState, useEffect, useRef } from 'react'
import styled from 'styled-components'
import {
  t, Panel, PanelHead, PanelTitle, Btn, Muted, Spinner, Pill, Cluster, Empty,
  Input, Label, Select, useUI,
} from './ui'
import { montant } from './format'
import {
  LOGISTIQUE, coutTrajet, coutApprovisionnement, coutMarchandise, taxeVente, tauxTaxe, taxeFourrage, camionsDeLignes,
  categorieProduit, lienItineraire, lienItineraireComplet,
} from './logistique'
import { supabase } from '../../supabase'
import { ecrire, lire } from './erreurs'

const Table = styled.div`
  border: 1px solid ${t.line};
  border-radius: ${t.rSm};
  overflow: hidden;
`
const Ligne = styled.div`
  display: grid;
  grid-template-columns: 1.4fr repeat(4, 1fr);
  gap: 10px;
  padding: 11px 14px;
  align-items: center;
  font-family: ${t.font};
  border-bottom: 1px solid ${t.lineSoft};
  background: ${(p) => (p.$best ? t.greenWash : t.surface)};
  &:last-child { border-bottom: none; }

  @media (max-width: 760px) {
    grid-template-columns: 1fr 1fr;
    row-gap: 6px;
  }
`
const Entete = styled(Ligne)`
  background: ${t.surfaceAlt};
  font-size: 0.62rem; font-weight: 600;
  letter-spacing: 0.075em; text-transform: uppercase; color: ${t.textFaint};
  @media (max-width: 760px) { display: none; }
`
const Nom = styled.div`
  font-size: 0.85rem; font-weight: 600; color: ${t.text};
  letter-spacing: -0.014em;
  display: flex; align-items: center; gap: 7px; flex-wrap: wrap;
`
const Lien = styled.a`
  font-size: 0.82rem; color: ${t.gold};
  font-variant-numeric: tabular-nums; font-weight: 500;
  text-decoration: none;
  border-bottom: 1px dashed rgba(15,92,77,0.35);
  &:hover { border-bottom-style: solid; }
  @media (max-width: 760px) {
    &::before {
      content: attr(data-label) ' ';
      font-size: 0.62rem; text-transform: uppercase; letter-spacing: 0.07em;
      color: ${t.textFaint}; font-weight: 600;
    }
  }
`
const Val = styled.div`
  font-size: 0.82rem; color: ${(p) => p.$c || t.textSoft};
  font-variant-numeric: tabular-nums;
  font-weight: ${(p) => (p.$fort ? 650 : 500)};
  @media (max-width: 760px) {
    &::before {
      content: attr(data-label) ' ';
      font-size: 0.62rem; text-transform: uppercase; letter-spacing: 0.07em;
      color: ${t.textFaint}; font-weight: 600;
    }
  }
`
const Synthese = styled.div`
  margin-top: 14px; padding: 14px 16px;
  background: ${t.surfaceAlt};
  border: 1px solid ${t.line};
  border-radius: ${t.rSm};
  font-family: ${t.font};
`
const SyntheseTitre = styled.div`
  font-size: 0.62rem; font-weight: 600; letter-spacing: 0.075em;
  text-transform: uppercase; color: ${t.textFaint}; margin-bottom: 12px;
`
const SLigne = styled.div`
  display: flex; justify-content: space-between; align-items: baseline; gap: 12px;
  padding: 5px 0; font-size: 0.82rem; color: ${t.textSoft};
  b { font-weight: 600; color: ${t.text}; font-variant-numeric: tabular-nums; }
`
const STotal = styled(SLigne)`
  margin-top: 8px; padding-top: 10px;
  border-top: 1px solid ${t.line};
  span { font-weight: 600; color: ${t.text}; }
  b { font-family: ${t.serif}; font-size: 1.06rem; font-weight: 650; letter-spacing: -0.03em; color: ${t.gold}; }
`
const SParTonne = styled.div`
  margin-top: 7px; font-size: 0.78rem; color: ${t.textSoft};
  b { font-weight: 650; color: ${t.green}; font-variant-numeric: tabular-nums; }
`
/* Réglages du calcul. Grille propre plutôt que des colonnes empilées :
   `min-width: 0` est indispensable, sinon la largeur minimale du menu
   déroulant (celle de son option la plus longue) écrase la colonne
   voisine et déforme la ligne entière. */
const Reglages = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 148px 96px 108px;
  gap: 12px;
  margin-bottom: 16px;
  > * { min-width: 0; }

  @media (max-width: 700px) { grid-template-columns: 1fr; }
`
const Prix = styled.div`
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  > * { min-width: 0; }
`
const Champ = styled.div`
  display: flex; flex-direction: column; gap: 5px;

  /* Libellé sur une seule ligne : coupé plutôt que replié, sinon la
     hauteur des champs voisins se décale. */
  label {
    margin-bottom: 0;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
`
const Unite = styled.span`
  font-weight: 500; letter-spacing: 0.04em; color: ${t.textFaint};
  text-transform: none;
`

/* La marge : deux chiffres qu'on veut lire d'un coup d'œil, et une
   couleur qui dit tout de suite si l'affaire est bonne. */
const Marge = styled.div`
  margin-top: 12px; padding-top: 12px;
  border-top: 1px solid ${t.line};
  display: flex; justify-content: space-between; align-items: baseline; gap: 12px;
  font-size: 0.82rem; color: ${t.textSoft};
  strong {
    font-family: ${t.serif}; font-size: 1.18rem; font-weight: 650;
    letter-spacing: -0.03em; font-variant-numeric: tabular-nums;
    color: ${(p) => (p.$positive ? t.green : t.red)};
  }
`
const MargeDetail = styled.div`
  margin-top: 5px; font-size: 0.76rem; color: ${t.textFaint};
  font-variant-numeric: tabular-nums;
  b { font-weight: 600; color: ${(p) => (p.$positive ? t.green : t.red)}; }
`

const Params = styled.div`
  display: flex; flex-wrap: wrap; gap: 6px 14px;
  margin-top: 12px;
  font-family: ${t.font}; font-size: 0.7rem; color: ${t.textFaint};
  b { font-weight: 600; color: ${t.textSoft}; }
`
const Erreur = styled.div`
  background: ${t.redWash};
  border: 1px solid #F0D8D3;
  border-radius: ${t.rSm};
  padding: 11px 14px;
  font-family: ${t.font}; font-size: 0.79rem; color: ${t.red};
  line-height: 1.5;
`

/* Les distances ne changent pas d'une consultation à l'autre : on les
   garde le temps de la session pour ne pas refaire attendre ni
   reconsommer du quota sur un dossier déjà calculé.

   L'empreinte de la configuration entre dans la clé : sans elle, un
   déplacement de douane laissait les anciens kilomètres à l'écran
   sans aucun moyen de s'en apercevoir. */
function empreinteConfig() {
  const brut = JSON.stringify([
    LOGISTIQUE.depart.coords,
    LOGISTIQUE.douanes.map((d) => d.coords),
    LOGISTIQUE.allerRetour,
    LOGISTIQUE.profil,
    LOGISTIQUE.preference,
  ])
  let h = 0
  for (let i = 0; i < brut.length; i++) h = (h * 31 + brut.charCodeAt(i)) | 0
  return Math.abs(h).toString(36)
}

const cleCache = (dest, pays) => `cout-livraison:${empreinteConfig()}:${pays}:${dest}`

function lireCache(dest, pays) {
  try { return JSON.parse(sessionStorage.getItem(cleCache(dest, pays)) || 'null') } catch { return null }
}

/* `cible` dit où figer le calcul : { table: 'quotes' | 'orders', id }.
   Une commande saisie à la main n'a pas de devis, elle porte donc son
   propre coût plutôt que d'être écartée des statistiques. */
export default function CoutLivraison({ destination, lignesDevis, vente, cible, coutEnregistre, onEnregistre }) {
  const enCache = destination ? lireCache(destination, 'CH') : null
  const [etat, setEtat] = useState(enCache ? 'ok' : 'repos')
  const [erreur, setErreur] = useState('')
  const { toast } = useUI()
  const [fournisseurs, setFournisseurs] = useState([])
  const [choix, setChoix] = useState(coutEnregistre?.fournisseur_id || '')
  const [prix, setPrix] = useState(coutEnregistre?.prix || { ...LOGISTIQUE.prixDefaut })
  const [kmAppro, setKmAppro] = useState(
    coutEnregistre?.km_approvisionnement != null ? String(coutEnregistre.km_approvisionnement) : '',
  )
  const [figeLe, setFigeLe] = useState(coutEnregistre?.calcule_le || null)
  const [pays, setPays] = useState(coutEnregistre?.pays || 'CH')
  const [camionsSaisis, setCamionsSaisis] = useState(
    coutEnregistre?.camions != null ? String(coutEnregistre.camions) : '',
  )

  useEffect(() => {
    lire(
      supabase.from('fournisseurs').select('*').eq('actif', true).order('nom'),
      toast, 'chargement des fournisseurs',
    ).then((data) => { if (data) setFournisseurs(data) })
  }, [toast])

  /* Choisir un fournisseur pré-remplit ; les champs restent modifiables
     pour tenir compte de la qualité ou de la saison. */
  const choisir = (id) => {
    setChoix(id)
    const f = fournisseurs.find((x) => x.id === id)
    if (!f) return
    setPrix({
      paille: f.prix_paille ?? '',
      foin: f.prix_foin ?? '',
      luzerne: f.prix_luzerne ?? '',
    })
    if (f.km_approvisionnement != null) setKmAppro(String(f.km_approvisionnement))
  }
  const [resultat, setResultat] = useState(enCache)

  const configIncomplete =
    !LOGISTIQUE.depart.coords || LOGISTIQUE.douanes.some((d) => !d.coords)

  /* Basculer le pays remet le trajet à zéro : un itinéraire par
     Vallorbe n'a aucun sens pour une livraison en France, et le laisser
     à l'écran ferait croire à un chiffre valable. */
  const changerPays = (p) => {
    if (p === pays) return
    setPays(p)
    const cache = destination ? lireCache(destination, p) : null
    setResultat(cache)
    setEtat(cache ? 'ok' : 'repos')
  }

  const calculer = async () => {
    setEtat('calcul'); setErreur('')
    try {
      const r = await fetch('/api/itineraire', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          destination,
          depart: LOGISTIQUE.depart,
          /* Aucune douane pour la France : pas de frontière, donc pas de
             RPLP, et un trajet direct au lieu d'une comparaison. */
          douanes: pays === 'FR' ? [] : LOGISTIQUE.douanes,
          pays,
          profil: LOGISTIQUE.profil,
          preference: LOGISTIQUE.preference,
        }),
      })
      if (r.status === 404) {
        throw new Error(
          "la fonction /api/itineraire n'est pas servie. En local, lancez « npx vercel dev » "
          + 'au lieu de « npm start » — le serveur de développement de CRA ne sert pas les fonctions.',
        )
      }
      const j = await r.json()
      if (!r.ok) throw new Error(j.erreur || `erreur ${r.status}`)
      setResultat(j)
      try { sessionStorage.setItem(cleCache(destination, pays), JSON.stringify(j)) } catch { /* mode privé */ }
      setEtat('ok')
    } catch (e) {
      setErreur(e.message)
      setEtat('erreur')
    }
  }

  /* Le calcul automatique sert de proposition ; un chargement mixte
     peut tenir sur moins de camions que la règle ne le suppose. */
  const camionsAuto = camionsDeLignes(lignesDevis)
  const camions = Math.max(1, parseInt(camionsSaisis, 10) || camionsAuto)

  const lignes = (resultat?.trajets || [])
    .map((tr) => {
      const douane = LOGISTIQUE.douanes.find((d) => d.label === tr.label)
      /* Sans douane — livraison en France — il n'y a qu'un seul tronçon :
         les liens pointent tous vers l'itinéraire direct plutôt que de
         se construire sur des coordonnées absentes, ce qui les rendait
         inertes. */
      const direct = !douane
      return {
        ...tr,
        ...coutTrajet(tr, LOGISTIQUE, camions),
        lienFrance: direct
          ? lienItineraire(LOGISTIQUE.depart.coords, resultat?.destCoords)
          : lienItineraire(LOGISTIQUE.depart.coords, douane.coords),
        lienSuisse: direct ? null : lienItineraire(douane.coords, resultat?.destCoords),
        lienComplet: direct
          ? lienItineraire(LOGISTIQUE.depart.coords, resultat?.destCoords)
          : lienItineraireComplet(LOGISTIQUE.depart.coords, douane.coords, resultat?.destCoords),
      }
    })
    /* La douane la moins chère en tête : c'est elle qui sert au coût. */
    .sort((a, b) => a.total - b.total)

  /* Le coût est recalculé à chaque frappe puis figé sur le devis sans
     intervention. Ce sont bien des valeurs recopiées : modifier plus tard
     le tarif d'un fournisseur ne touchera pas ce devis. */
  const synthese = (() => {
    const trajet = lignes[0]
    if (!trajet) return null
    const appro = coutApprovisionnement(kmAppro, LOGISTIQUE, camions)
    const achat = coutMarchandise(lignesDevis, prix)
    const tonnage = (lignesDevis || []).reduce((a, l) => a + (parseFloat(l.poids) || 0), 0)
    /* La vente ne compte que si le devis est chiffré : un devis vide
       afficherait sinon une perte égale au coût. */
    const ca = parseFloat(vente) || 0
    /* La taxe suit le montant facturé, elle est donc nulle tant que le
       devis n'est pas chiffré. */
    const taxe = taxeVente(ca, pays)
    /* Refacturée au client mais versée par nous : elle est dans le CA
       comme dans les charges, et n'influe donc pas sur la marge — la
       faire disparaître des deux côtés donnerait un coût sous-évalué. */
    const fourrage = taxeFourrage(lignesDevis)
    const total = achat + appro.carburant + trajet.total + taxe + fourrage
    const marge = ca > 0 ? ca - total : null
    const f = fournisseurs.find((x) => x.id === choix)
    return {
      appro, achat, tonnage, total, taxe, fourrage, livraison: trajet.total, ca, marge,
      instantane: {
        calcule_le: new Date().toISOString(),
        fournisseur_id: choix || null,
        fournisseur_nom: f?.nom || null,
        prix: { ...prix },
        km_approvisionnement: appro.km,
        tonnage,
        douane: trajet.label,
        km_douane: trajet.kmFrance,
        km_suisse: trajet.kmSuisse,
        camions,
        pays,
        achat,
        carburant_appro: appro.carburant,
        carburant_livraison: trajet.carburant,
        rplp: trajet.rplp,
        taxe, taux_taxe: tauxTaxe(pays),
        taxe_fourrage: fourrage,
        total,
        cout_tonne: tonnage > 0 ? total / tonnage : null,
        vente: ca || null,
        marge,
      },
    }
  })()

  /* La date de calcul change à chaque rendu : on compare le reste, sinon
     l'enregistrement se relancerait en boucle. */
  const empreinte = synthese
    ? JSON.stringify({ ...synthese.instantane, calcule_le: null })
    : null
  const dejaEcrit = useRef(
    coutEnregistre ? JSON.stringify({ ...coutEnregistre, calcule_le: null }) : null,
  )

  useEffect(() => {
    if (!cible?.id || !empreinte || empreinte === dejaEcrit.current) return
    /* Court délai : on écrit une fois la saisie posée, pas à chaque touche. */
    const minuteur = setTimeout(async () => {
      dejaEcrit.current = empreinte
      const ok = await ecrire(
        supabase.from(cible.table).update({ cout_revient: synthese.instantane }).eq('id', cible.id),
        toast, 'enregistrement du coût',
      )
      if (ok) { setFigeLe(synthese.instantane.calcule_le); onEnregistre?.(synthese.instantane) }
      else dejaEcrit.current = null   // on retentera à la prochaine modification
    }, 700)
    return () => clearTimeout(minuteur)
  }, [empreinte, cible?.table, cible?.id])  // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Panel>
      <PanelHead>
        <PanelTitle $sm>🚛 Coût de revient estimé</PanelTitle>
        {destination && !configIncomplete && (
          <Btn $variant="ghost" $size="sm" onClick={calculer} disabled={etat === 'calcul'}>
            {etat === 'calcul' ? <><Spinner /> Calcul en cours…</> : etat === 'ok' ? '↻ Recalculer' : 'Calculer'}
          </Btn>
        )}
      </PanelHead>

      {!destination ? (
        <Muted>
          Aucune adresse exploitable dans cette demande. Le calcul a besoin d'au moins
          une localité et un canton.
        </Muted>
      ) : configIncomplete ? (
        <Erreur>
          Adresse de départ ou bureaux de douane non renseignés dans
          <b> src/pages/admin/logistique.js</b>. Le calcul reste indisponible tant
          qu'ils ne sont pas définis.
        </Erreur>
      ) : (
        <>
          <Muted style={{ marginBottom: 12 }}>
            Vers <strong style={{ color: t.text }}>{destination}</strong>
            {LOGISTIQUE.allerRetour && ' — aller-retour, retour à vide compris'}
          </Muted>

          {etat === 'calcul' && (
            <Muted style={{ fontSize: '0.78rem' }}>
              Recherche de l'adresse puis calcul des itinéraires poids lourd — quelques secondes.
            </Muted>
          )}

          {etat === 'erreur' && <Erreur>{erreur}</Erreur>}

          {etat === 'ok' && lignes.length > 0 && (
            <>
              <Table>
                <Entete>
                  <div>{pays === 'FR' ? 'Itinéraire' : 'Bureau de douane'}</div>
                  <div>{pays === 'FR' ? 'Aller' : 'Départ → douane'}</div>
                  <div>{pays === 'FR' ? '—' : 'km Suisse'}</div>
                  <div>Carburant</div>
                  <div>RPLP</div>
                </Entete>
                {lignes.map((l, i) => (
                  <Ligne key={l.label} $best={i === 0}>
                    <Nom>
                      <Lien href={l.lienComplet} target="_blank" rel="noopener noreferrer"
                        title={`Ouvrir l'itinéraire complet ${LOGISTIQUE.depart.label} → ${l.label} → client dans Plans`}>
                        {l.label}
                      </Lien>
                      {i === 0 && <Pill $tone="green">le moins cher</Pill>}
                    </Nom>
                    <Lien data-label="Départ → douane" href={l.lienFrance} target="_blank" rel="noopener noreferrer"
                      title={`Ouvrir ${LOGISTIQUE.depart.label} → ${l.lienSuisse ? l.label : 'client'} dans Plans`}>
                      {l.kmFrance.toFixed(0)} km
                    </Lien>
                    {l.lienSuisse ? (
                      <Lien data-label="km Suisse" href={l.lienSuisse} target="_blank" rel="noopener noreferrer"
                        title={`Ouvrir ${l.label} → destination dans Plans`}>
                        {l.kmSuisse.toFixed(0)} km
                      </Lien>
                    ) : (
                      <Val data-label="km Suisse" style={{ color: t.textFaint }}>—</Val>
                    )}
                    <Val data-label="Carburant">{montant(l.carburant)}</Val>
                    <Val data-label="RPLP" $c={t.blue}>{montant(l.rplp)}</Val>
                  </Ligne>
                ))}
              </Table>

              <Cluster style={{ marginTop: 12, justifyContent: 'space-between' }}>
                <Muted style={{ fontSize: '0.75rem' }}>
                  Via <strong style={{ color: t.text }}>{lignes[0].label}</strong> —
                  {' '}{lignes[0].kmTotal.toFixed(0)} km au total
                  {camions > 1 ? `, ${camions} camions` : ''}
                </Muted>
                <Nom style={{ fontSize: '1.02rem', color: t.green }}>
                  {montant(lignes[0].total)}
                </Nom>
              </Cluster>

              {synthese && (
                  <Synthese>
                    <SyntheseTitre>Coût de revient</SyntheseTitre>

                    <Reglages>
                      <Champ>
                        <Label>Fournisseur</Label>
                        <Select value={choix} onChange={(e) => choisir(e.target.value)}>
                          <option value="">— Prix saisis à la main —</option>
                          {fournisseurs.map((f) => <option key={f.id} value={f.id}>{f.nom}</option>)}
                        </Select>
                      </Champ>
                      <Champ>
                        <Label>Pays livré</Label>
                        <Select value={pays} onChange={(e) => changerPays(e.target.value)}>
                          <option value="CH">Suisse — douane et RPLP</option>
                          <option value="FR">France — sans RPLP</option>
                        </Select>
                      </Champ>
                      <Champ>
                        <Label>Camions</Label>
                        <Input type="number" min="1" value={camionsSaisis}
                          onChange={(e) => setCamionsSaisis(e.target.value)}
                          placeholder={String(camionsAuto)} />
                      </Champ>
                      <Champ>
                        <Label>Km appro.</Label>
                        <Input type="number" value={kmAppro}
                          onChange={(e) => setKmAppro(e.target.value)}
                          placeholder={String(LOGISTIQUE.kmApprovisionnement)} />
                      </Champ>

                      <Prix>
                        {['paille', 'foin', 'luzerne'].map((cat) => (
                          <Champ key={cat}>
                            <Label>{cat} <Unite>€/T</Unite></Label>
                            <Input type="number" step="0.01" value={prix[cat] ?? ''}
                              onChange={(e) => setPrix((p) => ({ ...p, [cat]: e.target.value }))}
                              placeholder="—" />
                          </Champ>
                        ))}
                      </Prix>
                    </Reglages>

                    <div style={{ marginTop: 14 }}>
                      {(lignesDevis || []).filter((l) => parseFloat(l.poids) > 0).map((l, k) => (
                        <SLigne key={k}>
                          <span>{l.produit} — {l.poids} T × {prix[categorieProduit(l.produit)] || 0} €</span>
                          <b>{montant((parseFloat(l.poids) || 0) * (parseFloat(prix[categorieProduit(l.produit)]) || 0))}</b>
                        </SLigne>
                      ))}
                      <SLigne><span>
                          Approvisionnement — {synthese.appro.km} km
                          {camions > 1 ? ` × ${camions} camions` : ''}
                        </span><b>{montant(synthese.appro.carburant)}</b></SLigne>
                      <SLigne><span>
                          Livraison via {lignes[0].label} — carburant et RPLP
                          {camions > 1 ? ` · ${camions} camions` : ''}
                        </span><b>{montant(synthese.livraison)}</b></SLigne>
                      {synthese.fourrage > 0 && (
                        <SLigne>
                          <span>Taxe fourrage — douane suisse, reversée</span>
                          <b>{montant(synthese.fourrage)}</b>
                        </SLigne>
                      )}
                      {synthese.taxe > 0 && (
                        <SLigne>
                          <span>Taxe {tauxTaxe(pays)} % sur le montant facturé</span>
                          <b>{montant(synthese.taxe)}</b>
                        </SLigne>
                      )}

                      <STotal><span>Total</span><b>{montant(synthese.total)}</b></STotal>
                      {synthese.tonnage > 0 && (
                        <SParTonne>
                          soit <b>{montant(synthese.total / synthese.tonnage)}</b> la tonne sur
                          {' '}{synthese.tonnage} T — prix de vente à fixer au-dessus
                        </SParTonne>
                      )}
                    </div>

                    {synthese.marge != null && (
                      <>
                        <Marge $positive={synthese.marge >= 0}>
                          <span>Marge sur une vente de {montant(synthese.ca)}</span>
                          <strong>{montant(synthese.marge, { signe: true })}</strong>
                        </Marge>
                        <MargeDetail $positive={synthese.marge >= 0}>
                          <b>{(synthese.marge / synthese.ca * 100).toFixed(1)} %</b> du chiffre de vente
                          {synthese.tonnage > 0 && <> · <b>{montant(synthese.marge / synthese.tonnage)}</b> la tonne</>}
                        </MargeDetail>
                      </>
                    )}

                    {cible?.id && figeLe && (
                      <Muted style={{ fontSize: '0.72rem', marginTop: 12, display: 'block' }}>
                        ✓ figé sur le devis le {new Date(figeLe).toLocaleString('fr-FR', {
                          day: '2-digit', month: '2-digit', year: 'numeric',
                          hour: '2-digit', minute: '2-digit',
                        })}
                        {synthese.instantane.fournisseur_nom ? ` · ${synthese.instantane.fournisseur_nom}` : ''}
                      </Muted>
                    )}
                  </Synthese>
              )}

              <Params>
                <span>Le nom de la douane ouvre l'itinéraire complet dans Plans, chaque kilométrage ouvre son tronçon</span>
                <span><b>{LOGISTIQUE.conso}</b> L/100 km</span>
                <span>gasoil <b>{LOGISTIQUE.prixGasoil.toFixed(2)} €</b> HT</span>
                <span>itinéraires <b>{LOGISTIQUE.profil === 'driving-hgv' ? 'poids lourd' : 'voiture'}</b>, {LOGISTIQUE.preference === 'shortest' ? 'au plus court' : 'au plus rapide'}</span>
                {pays === 'FR' ? (
                  <span><b>Livraison en France</b> : aucune RPLP, la redevance ne frappe que le réseau suisse</span>
                ) : (
                  <span>RPLP <b>{LOGISTIQUE.poidsRplp} t × {LOGISTIQUE.tarifRplp} ct/tkm</b> (Euro 6), sur le tronçon douane → client</span>
                )}
              </Params>
            </>
          )}

          {etat === 'repos' && (
            <Empty icon="🧮" title="Pas encore calculé">
              Le calcul interroge un service d'itinéraires : lancez-le quand vous en avez besoin,
              plutôt qu'à chaque ouverture du dossier.
            </Empty>
          )}
        </>
      )}
    </Panel>
  )
}
